import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useToggle } from '../../generic/hooks/useToggle'
import SmartReactflow from './smartreactflow/SmartReactflow'
import getWorkflowNodesAndEdges, { uniqueOperationNodeCode } from './utils'
import { apiPost } from '../../generic/Api_Functions'
import getRandomUUID from '../../generic/utilities/randomuuid/RandomUUID'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { useWorkflowHierarchy } from './WorkflowRecursionDetector'
import { asCopiedJobPreview, asDroppedOperationJob, copyOfEditableContent, createNewJobReference } from './newJobReference'
import { getOperationPalette, getOperationVersionDetail, readOperationLibraryError } from './operationLibraryApi'
import { PROCESS_ROW_DROP_KIND } from './processPaletteTree'
import { buildCompositionCensus } from './compositionCensus'
import { applyHydratedSubWorkflows, collectUnhydratedSets, fetchWorkflowForProcessTree, hydrateProcessTree } from './processTreeHydration'
import {
  applyProcessOriginHighlight,
  assignProcessOriginColors,
  buildProcessOriginLegend,
  collectProcessOriginCodes,
  processOriginColorByNodeId,
  toSourceProcessPair
} from './processOriginHighlight'
import ProcessOriginLegend from './ProcessOriginLegend'
import ProcessLibraryPanel from './ProcessLibraryPanel'
import ProcessMiniTree from './ProcessMiniTree'
import LibraryFamilies from './LibraryFamilies'
import { useProcessLibraryPanelPreferences } from './useProcessLibraryPanelPreferences'
import { PANEL_TAB } from './processLibraryPanelPreferences'
import { CANVAS_DROP_TARGET, DROP_TARGET_KIND, NO_DROP_TARGET, destinationOf } from './miniTreeRows'
import { applyInsertionToEdges, edgeOfNodeResult, planInsertAfter, positionRightOf } from './insertAfterPlan'

/**
 * Historia 152 (S24 · `RN-01.11`, DL-03): al soltar varias filas del árbol a la vez EN EL LIENZO los nodos se
 * depositan EN CASCADA desde el punto de soltado, con este desplazamiento fijo, para que ninguno tape a otro.
 * No se conectan entre sí (`RN-G8`). Sobre una fila del mini árbol se encadenan (historia 167, RN-08.7).
 */
const MULTI_DROP_OFFSET = 40

/** Historia 152 (S79) + 167 (E-16): la caché de subworkflows del censo y del mini árbol, vacía y tolerante. */
const EMPTY_CENSUS_HYDRATION = { subWorkflowsById: {}, failedSetNodeIds: [], failedMessageByNodeId: {} }

/** Historia 167 (RN-09): no hay arrastre de la biblioteca en curso. */
const NO_LIBRARY_DRAG = { active: false, label: '' }

// `prefix` (story 204, P12): the text the generated code starts with when it is not the counting key itself — a
// technology counts by its raw name (`referenceName`, the same in every language) and reads in the creator's language.
const generateDefaultName = (nodeName, nodeCounters, prefix = nodeName) => {
  if (!nodeCounters[nodeName]) {
    nodeCounters[nodeName] = 1
  }
  const nodeIndex = nodeCounters[nodeName]++
  return prefix + '-' + nodeIndex
}

/** Lo que se suelta, leído del `dataTransfer` en síncrono: después de un `await` el navegador ya no lo deja leer. */
const readDropPayload = (dataTransfer) => ({
  type: dataTransfer.getData('application/reactflow/nodeType'),
  subType: dataTransfer.getData('application/reactflow/subType'),
  // D12 (`IN16`): el par se normaliza aquí — los dos o ninguno —; el `dataTransfer` solo lleva cadenas.
  ...toSourceProcessPair(
    dataTransfer.getData('application/reactflow/sourceProcessCode'),
    dataTransfer.getData('application/reactflow/sourceProcessVersionNumber')
  )
})

/** En el lienzo, en cascada desde el punto de soltado, como hoy (#152 S24). */
const cascadeFrom = (position) => (index) => ({ x: position.x + index * MULTI_DROP_OFFSET, y: position.y + index * MULTI_DROP_OFFSET })
const NOT_CREATED = { id: '', code: '' }

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowEditingDataAction} WorkflowEditingDataAction EditingDataAction object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowEditingData} WorkflowEditingData EditingData object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node Node object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} JobReference JobReference object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').EdgeData} EdgeData EdgeData object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').NodeResult} NodeResult NodeResult object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').ProgressCalculationType} ProgressCalculationType ProgressCalculationType object
 */

/**
 *
 * @param {Object} props Props object
 * @param {WorkflowEditingData} props.workflowData EditingData object
 * @param {React.Dispatch<WorkflowEditingDataAction>} props.dispatchWorkflowData EditingDataAction object
 * @param {boolean} props.disableEdges Param use to set if edges are disabled or not. Default value false
 * @param {boolean} props.updateReactFlow Param to force update ReactFlow
 * @param {function(boolean)} props.setUpdateReactFlow Function to change updateReactFlow value
 * @param {import('reactflow').Node[]} props.nodes Nodes array
 * @param {React.Dispatch<React.SetStateAction<import('reactflow').Node[]>>} props.setNodes Set nodes function
 * @param {import('reactflow').OnNodesChange} props.onNodesChange On nodes change function
 * @param {import('reactflow').Edge[]} props.edges Edges array
 * @param {React.Dispatch<React.SetStateAction<import('reactflow').Edge[]>>} props.setEdges Set edges function
 * @param {import('reactflow').OnEdgesChange} props.onEdgesChange On edges change function
 * @param {ProgressCalculationType[]} props.progressCalculationTypes Progress calculation types array
 * @param {import('../../generic/utilities/objectStructures/globalStructures').IdName[]} props.indexValues
 * @returns
 */
const WorkflowFlow = ({
  workflowData,
  dispatchWorkflowData,
  disableEdges = false,
  updateReactFlow,
  setUpdateReactFlow,
  nodes,
  setNodes,
  onNodesChange,
  edges,
  setEdges,
  onEdgesChange,
  progressCalculationTypes,
  onSubWorkflowDoubleClick,
  frontendConfig,
  showAlert,
  indexValues,
  fabricationOrderOperable = true,
  // Historia 167 (PO, 29/09/2026): lo que abre el doble clic en una fila del mini árbol, en los acordeones del editor.
  onOpenJobProperties = () => {},
  onOpenJobResources = () => {}
}) => {
  const [fitView, toogleFitView] = useToggle()
  const severalLinks = frontendConfig?.workflowNodeSerialLinks === true
  // Historia 167 (HU-02 · RN-04): lo que el panel recuerda en el navegador. Sustituye al `useState(true)` de
  // la paleta, que nacía abierta en cada montaje. Sin edición el panel está plegado y lo recordado no se
  // toca (RN-04.6): por eso ya no hay efecto que lo cierre al salir de edición.
  const [panelPreferences, updatePanelPreferences] = useProcessLibraryPanelPreferences()
  const panelOpen = workflowData.editing && panelPreferences.open
  const [technologies, setTechnologies] = useState([])
  const [workflows, setWorkflows] = useState([])
  const { t_ } = useSmartTranslation()
  const { canAddWorkflow } = useWorkflowHierarchy()
  // Historia 152 (S79 · RN-03.2, IN13): los subworkflows que el censo —y ahora el mini árbol— necesitan leer,
  // cargados uno a uno y tolerantemente con la máquina de #118. Viven en el estado LOCAL de la vista y se
  // superponen al modelo en cada render: escribirlos en el reducer haría viajar el censo en el `PUT`.
  const [censusHydration, setCensusHydration] = useState(EMPTY_CENSUS_HYDRATION)
  const censusHydratingRef = useRef(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // Historia 152 (S86/S87 · RN-03.9, DL-04): el resaltado por proceso de origen es ESTADO DE LA SESIÓN.
  const [processOriginHighlightOn, setProcessOriginHighlightOn] = useState(false)
  const processOriginColorsRef = useRef({})
  // Historia 167 (HU-05 · RN-09, R2): el arrastre de la biblioteca en curso vive aquí y no en el
  // `dataTransfer`, que el navegador no deja leer durante `dragover`. El cursor sobre el lienzo se guarda
  // como mucho una vez por fotograma.
  const [libraryDrag, setLibraryDrag] = useState(NO_LIBRARY_DRAG)
  const libraryDragRef = useRef(NO_LIBRARY_DRAG)
  libraryDragRef.current = libraryDrag
  const [dropTarget, setDropTarget] = useState(NO_DROP_TARGET)
  // D16: el alto del panel va de 180 px al alto del lienzo, así que hay que medirlo.
  const canvasContainerRef = useRef()
  const [canvasHeight, setCanvasHeight] = useState(0)

  useLayoutEffect(() => {
    const container = canvasContainerRef.current
    const observer = new ResizeObserver(() => setCanvasHeight(container.clientHeight))
    observer.observe(container)
    setCanvasHeight(container.clientHeight)
    return () => observer.disconnect()
  }, [])

  /**
   * Historia 167 (D12, D16.8): las familias piden páginas de 20 y lo cargado se ACUMULA aquí, porque el
   * soltado busca la tecnología o la plantilla soltada entre todo lo cargado (hoy solo había una página de 5
   * y una plantilla de la página anterior no se encontraba al soltar, impacto R5).
   */
  const fetchTechnologyPage = useCallback(async (pagination) => {
    const page = await apiPost('technologiesWorkflow', pagination)
    // `technologiesWorkflow` responde 200 con cuerpo nulo ante un error (impacto R8): la familia lo trata
    // como fallo; aquí solo no se rompe.
    const content = Array.isArray(page?.content) ? page.content : []
    setTechnologies((loaded) => (pagination.page > 0 ? loaded.concat(content) : content))
    return page
  }, [])

  const fetchTemplatePage = useCallback(async (pagination) => {
    // RN01.6 (HU-01): the template identifier is workflow.code; the list is ordered by code and version.
    const page = await apiPost('getWorkflowTemplates', { ...pagination, sortKey: ['code', 'version'], sortMode: [1, 1] })
    const content = Array.isArray(page?.content) ? page.content : []
    setWorkflows((loaded) => (pagination.page > 0 ? loaded.concat(content) : content))
    return page
  }, [])

  /**
   * Historia 152 (S79 · RN-03.2, D9): el modelo VIVO con los subworkflows cargados encima. La
   * superposición se rehace en cada render y NUNCA se escribe en el reducer (`IN13`, impacto R8):
   * es la misma disciplina que `ProcessTreeView` aplica desde #118, con la misma función.
   *
   * ⚠️ La dependencia es `workflowData` ENTERO, no `workflowData.workflow` (J7). El reducer del
   * editor muta el workflow EN SITIO —`ADD_NODE` empuja el nodo en el mismo `workflow.nodes` y
   * devuelve solo `{ ...oldData }`, y `DELETE_NODE` y las acciones de vínculo hacen lo propio—, de
   * modo que `workflow` y `workflow.nodes` CONSERVAN su identidad tras cada cambio pendiente. El
   * estado de edición es lo único que el reducer garantiza nuevo en cada acción; colgar de
   * `workflow` cualquier derivado lo congelaba hasta el siguiente `SET_WORKFLOW` —es decir, hasta
   * el guardado—, que es exactamente lo que S79/S84-S86 prohíben.
   *
   * Por el mismo motivo el workflow entra **copiado en superficie**: sin ningún subworkflow en la
   * caché, `applyHydratedSubWorkflows` devuelve tal cual el workflow que recibe (así está escrito
   * desde #118 y no se toca), con lo que el resultado heredaría la identidad congelada del modelo
   * y ni el censo ni la hidratación —que cuelgan de este valor— volverían a correr.
   */
  const censusWorkflow = useMemo(
    () => applyHydratedSubWorkflows({ ...workflowData.workflow }, censusHydration.subWorkflowsById),
    [workflowData, censusHydration.subWorkflowsById]
  )

  /**
   * Historia 152 (S79 · CU-19 A3): una pasada de hidratación por apertura del panel, TOLERANTE
   * A FALLOS. Lo que no carga se recuerda como fallido —no se vuelve a pedir— y deja el censo
   * declarado como PARCIAL; lo que carga se reutiliza. Sin la paleta abierta no se pide nada: el
   * censo solo lo mira la paleta.
   */
  useEffect(() => {
    if (!panelOpen || censusHydratingRef.current) {
      return undefined
    }
    const pending = collectUnhydratedSets(censusWorkflow?.nodes).filter((entry) =>
      !censusHydration.subWorkflowsById[entry.subWorkflowId] && !censusHydration.failedSetNodeIds.includes(entry.nodeId))
    if (pending.length === 0) {
      return undefined
    }
    censusHydratingRef.current = true
    const run = async () => {
      const result = await hydrateProcessTree(censusWorkflow, fetchWorkflowForProcessTree, censusHydration.subWorkflowsById)
      censusHydratingRef.current = false
      // What loaded is kept even if the model changed meanwhile (a drop always changes it): it is a cache by
      // subworkflow id, and discarding it left the census stale until the next edit (PO, 29/09/2026).
      if (!mountedRef.current) {
        return
      }
      setCensusHydration((previous) => ({
        subWorkflowsById: { ...previous.subWorkflowsById, ...result.subWorkflowsById },
        failedSetNodeIds: [...new Set([...previous.failedSetNodeIds, ...result.failedSetNodeIds])],
        failedMessageByNodeId: { ...previous.failedMessageByNodeId, ...result.failedMessageByNodeId }
      }))
    }
    run()
    return undefined
  }, [panelOpen, censusWorkflow, censusHydration])

  /**
   * Historia 152 (CA3.1/CA3.2 · S73, S79, S84): EL CENSO. Derivado del modelo vivo en cada
   * cambio —arrastrar, repetir y eliminar lo mueven EN EL ACTO, sin guardar— y consumido por la
   * paleta. No se cachea en el modelo, no se guarda y no existe endpoint que lo conteste (§7.1).
   */
  const compositionCensus = useMemo(
    () => buildCompositionCensus(censusWorkflow, { failedSetNodeIds: censusHydration.failedSetNodeIds }),
    [censusWorkflow, censusHydration.failedSetNodeIds]
  )

  // S86/S88: un color por código de proceso, estable durante la sesión de edición. Solo el primer
  // nivel: un nodo WORKFLOW se colorea por SU origen, no por el de los nodos que contiene.
  // J7: la dependencia es `workflowData`, por el mismo motivo que el censo — el reducer muta
  // `workflow.nodes` en sitio y un nodo recién soltado no cambiaría la identidad del array.
  const processOriginColors = useMemo(() => {
    const colors = assignProcessOriginColors(
      collectProcessOriginCodes(workflowData.workflow?.nodes),
      processOriginColorsRef.current
    )
    processOriginColorsRef.current = colors
    return colors
  }, [workflowData])

  const processOriginLegend = useMemo(
    () => buildProcessOriginLegend(workflowData.workflow?.nodes, processOriginColors),
    [workflowData, processOriginColors]
  )

  /**
   * S87 (`RN-03.9`, `IN14`): el resaltado se aplica sobre los nodos YA RENDERIZADOS —`data`
   * parcheado en sitio, jamás una reconstrucción, que volvería a disparar `fitView`— y nada más:
   * ni un `dispatch`, ni una selección, ni una posición. Apagado, retira su color y el grafo
   * vuelve exactamente a la presentación normal.
   */
  useEffect(() => {
    const colorByNodeId = processOriginHighlightOn
      ? processOriginColorByNodeId(workflowData.workflow?.nodes, processOriginColors)
      : {}
    const painted = applyProcessOriginHighlight(nodes, colorByNodeId)
    // Nada que cambiar (el interruptor apagado sobre un grafo sin colorear, o un grafo sin ningún
    // origen) ⇒ NO se toca el lienzo: ni un `setNodes` de más.
    if (painted !== nodes) {
      setNodes(painted)
    }
  }, [processOriginHighlightOn, processOriginColors, workflowData, nodes, setNodes])

  useEffect(() => {
    toogleFitView()
  }, [toogleFitView])

  useEffect(() => {
    return () => {
      dispatchWorkflowData({
        type: 'SET_CURRENT_NODE',
        node: null
      })
      setNodes([])
      setEdges([])
      setUpdateReactFlow(true)
    }
  }, [dispatchWorkflowData, setNodes, setEdges, setUpdateReactFlow])

  const onNodeSelectionChange = useCallback((nodeList) => {
    if (!nodeList || nodeList.length === 0) {
      dispatchWorkflowData({
        type: 'SET_CURRENT_NODE', node: null
      })
      return
    }

    if (nodeList.length > 0 && nodeList[0].type === 'technologyNode') {
      const node = workflowData.workflow.nodes.find((node) => node.id === nodeList[0]?.id)
      dispatchWorkflowData({
        type: 'SET_CURRENT_NODE',
        node
      })
      return
    }

    dispatchWorkflowData({
      type: 'SET_CURRENT_NODE',
      node: null
    })
  }, [workflowData.workflow?.nodes, dispatchWorkflowData])

  const createNodeDataJobReference = useCallback(
    /**
       * @param {string} code Node code (RN01.6: the historical functional identifier, was "name")
       * @param {string} name Node free, descriptive name (RN01.2, nullable, never a code substitute)
       * @param {JobReference} jobReference
       * @param {Object} nodeSkipData S26/S29/S31: skip mark + output-only trace read from the Node DTO
       * @param {boolean} nodeSkipData.skipped
       * @param {string} nodeSkipData.skipReason
       * @param {string} nodeSkipData.skippedBy Output-only trace, never sent back as source of truth
       * @param {string} nodeSkipData.skippedAt Output-only trace, never sent back as source of truth
       */
    (nodeId, code, name, jobReference, nodeSkipData = {}) => {
      return {
        editing: workflowData.editing,
        nodeCode: code,
        nodeName: name || '',
        referenceName: jobReference.technology.name,
        subWorkflow: null,
        nodeType: 'JOB',
        // S31/S32 (RN09.4/D5): default, off. Toggled directly via `setNodes` when a BOM job chip
        // is clicked (`Workflow.jsx#highlightJobNode`) — never through a full node data rebuild,
        // which would re-trigger `fitView` (pan/zoom), forbidden by RN09.4/RN09.5.
        jobHighlighted: false,
        rework: jobReference.rework,
        nodeId,
        severalLinks,
        technology: jobReference.technology,
        // S69 (CA14): the ONLY fact the node needs to paint itself purple. The external character is
        // the job's own mark, so the technology above plays no part in it. Absent mark reads false.
        external: jobReference.external === true,
        hasParts: jobReference.hasParts === true,
        // Historia 100 (S137, RN43/POL1): whether this jobReference is a dispatched external-circuit
        // job. CONFIRMED contract field (convergence, lote 17: Sun's `GET workflowByFabricationOrder/
        // {id}` populates `jobReference.dispatched`, `WorkflowRest#populateDispatched`) -- see
        // WorkflowFlowExternalJobDispatchGating.test.jsx. Absent reads as false (POL5-style additive
        // default): an older Sun, or a non-circuit job, never blocks Skip.
        dispatched: jobReference.dispatched === true,
        skipped: nodeSkipData.skipped === true,
        skipReason: nodeSkipData.skipReason || '',
        skippedBy: nodeSkipData.skippedBy || '',
        skippedAt: nodeSkipData.skippedAt || '',
        fabricationOrderOperable,
        updateNodeCode: (newCode) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_CODE', nodeCode: newCode, nodeId })
        },
        updateNodeName: (newName) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_NAME', nodeName: newName, nodeId })
        },
        updateNodeRework: (newRework) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_REWORK', nodeRework: newRework, nodeId })
        },
        deleteNode: (nodeId) => {
          dispatchWorkflowData({ type: 'DELETE_NODE', nodeId })
        },
        updateNodeTechnology: (technology) => {
          dispatchWorkflowData({ type: 'UPDATE_JOB_TECHNOLOGY', technology, nodeId })
        },
        updateNodeSkip: (skipReason) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_SKIP', nodeId, skipReason })
        },
        updateNodeReactivate: () => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_REACTIVATE', nodeId })
        }
      }
    }, [severalLinks, dispatchWorkflowData, workflowData.editing, fabricationOrderOperable])

  const createNodeDataSubWorkflow = useCallback(
    /**
       * @param {string} code Node code (RN01.6: the historical functional identifier, was "name")
       * @param {string} name Node free, descriptive name (RN01.2, nullable, never a code substitute)
       * @param {Workflow} subWorkflow
      */
    (nodeId, code, name, subWorkflow) => {
      return {
        editing: workflowData.editing,
        nodeCode: code,
        nodeName: name || '',
        nodeType: 'WORKFLOW',
        // S31/S32 (RN09.4/D5): default, off — see the JOB node data above for why this is never
        // wired reactively here.
        jobHighlighted: false,
        // RN01.6 (HU-01): the card's reference line shows the subworkflow's identifier (code).
        referenceName: subWorkflow.code,
        referenceId: subWorkflow.id,
        severalLinks,
        subWorkflowIds: subWorkflow.subWorkflowIds,
        updateNodeCode: (newCode) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_CODE', nodeCode: newCode, nodeId })
        },
        updateNodeName: (newName) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_NAME', nodeName: newName, nodeId })
        },
        updateNodeRework: (newRework) => {
          dispatchWorkflowData({ type: 'UPDATE_NODE_REWORK', nodeRework: newRework, nodeId })
        },
        deleteNode: (nodeId) => {
          dispatchWorkflowData({ type: 'DELETE_NODE', nodeId })
        }
      }
    }, [severalLinks, dispatchWorkflowData, workflowData.editing])

  /**
     *
     * @param {Object} edge
     * @param {EdgeData} edge.data
     * @returns {NodeResult} NodeResult object
     */
  const createNodeResult = (edge) => {
    return {
      id: edge.id,
      nodeId: edge.source,
      operationResult: edge.data.type === 'OK' ? 'OK' : 'FAIL',
      nextNode: edge.target === 'end' ? null : edge.target,
      connectivity: edge.data.connectivity,
      eventType: edge.data.eventType,
      eventValue: edge.data.eventValue,
      startDate: edge.data.startDate,
      timezone: edge.data.timezone
    }
  }

  /**
     * @param {import('reactflow').Edge} edge
   */
  const addEdge = (edge) => {
    if (edge.source === 'start') {
      dispatchWorkflowData({ type: 'SET_NODE_IS_FIRST', nodeId: edge.target, isFirst: true })
    }
    dispatchWorkflowData({ type: 'ADD_NODE_RESULT', nodeResult: createNodeResult(edge), nodeId: edge.source })
  }

  /**
     *
     * @param {Object} edge
     * @param {EdgeData} edge.data
  */
  const updateEdge = useCallback((edge) => {
    const node = workflowData.workflow.nodes.find(node => node.id === edge.source)
    if (!node) {
      return
    }
    const nodeResult = node.nodeResults.find(nodeResult => nodeResult.id === edge.id)
    if (!nodeResult) {
      return
    }

    nodeResult.eventType = edge.data.eventType
    nodeResult.connectivity = edge.data.connectivity
    nodeResult.eventValue = edge.data.eventValue
    nodeResult.startDate = edge.data.startDate
    nodeResult.timezone = edge.data.timezone

    dispatchWorkflowData({ type: 'UPDATE_NODE_RESULT', nodeResult })
  }, [workflowData.workflow.nodes, dispatchWorkflowData])

  const deleteEdge = useCallback(/**  @param {import('reactflow').Edge} edge */ (edge) => {
    if (edge.source === 'start') {
      dispatchWorkflowData({ type: 'SET_NODE_IS_FIRST', nodeId: edge.target, isFirst: false })
    }
    dispatchWorkflowData({ type: 'DELETE_NODE_RESULT', nodeResultId: edge.id })
  }, [dispatchWorkflowData])

  const calculatedWorkflow = useMemo(() => {
    if (!workflowData.workflow) {
      return null
    }
    const calculatedWorkflow = { ...workflowData.workflow }
    if (!calculatedWorkflow) {
      return null
    }
    toogleFitView()
    return calculatedWorkflow
  }, [workflowData.workflow, toogleFitView])

  useEffect(() => {
    if (!updateReactFlow) {
      return
    }

    if (!calculatedWorkflow) {
      setNodes([])
      setEdges([])
      return
    }

    const { nodes, edges } = getWorkflowNodesAndEdges({
      workflow: calculatedWorkflow,
      nodeData: (node, position) => node.nodeType === 'JOB'
        ? createNodeDataJobReference(node.id, node.code, node.name, node.jobReference, {
          skipped: node.skipped,
          skipReason: node.skipReason,
          skippedBy: node.skippedBy,
          skippedAt: node.skippedAt
        })
        : createNodeDataSubWorkflow(node.id, node.code, node.name, node.subWorkflow),
      onDeleteEdge: deleteEdge,
      onUpdateEdge: updateEdge
    })
    setNodes(nodes)
    setEdges(edges)
    setUpdateReactFlow(false)
    toogleFitView()
  }, [calculatedWorkflow, setNodes, setEdges, disableEdges, dispatchWorkflowData, deleteEdge, updateEdge, updateReactFlow, setUpdateReactFlow, createNodeDataJobReference, createNodeDataSubWorkflow, toogleFitView])

  const countNodeNames = useCallback((newNode) => {
    const nodeCount = {}
    if (newNode && !nodeCount[newNode]) {
      nodeCount[newNode] = 1
    }
    nodes.filter((node) => {
      return node.id !== 'start' && node.id !== 'end'
    }).forEach((node) => {
      if (!nodeCount[node.data.referenceName]) {
        nodeCount[node.data.referenceName] = 1
      } else {
        nodeCount[node.data.referenceName] = nodeCount[node.data.referenceName] + 1
      }
    })
    return nodeCount
  }, [nodes])

  const createNode = useCallback((id, code, jobReference, workflow, position, nodeType) => {
    return {
      id,
      name: code,
      type: 'technologyNode',
      position: { x: position.x, y: position.y },
      // S26/D6: a freshly dropped node has an auto-generated code and no descriptive name yet.
      data: nodeType === 'JOB' ? createNodeDataJobReference(id, code, '', jobReference) : createNodeDataSubWorkflow(id, code, '', workflow)
    }
  }, [createNodeDataJobReference, createNodeDataSubWorkflow])

  /**
   * S42/S43 (CA3.2 / RN-03.2, RN-03.3, G1/D-12): el nodo nace con el contenido de la versión y con
   * su PROPIO `code` de nodo, como cualquier nodo nuevo — el código de biblioteca no se copia ahí:
   * vive en `library_code`, que en un nodo de workflow es siempre nulo.
   *
   * El vínculo viaja como `operationLink` con intención BIND; nada se persiste hasta el guardado
   * general del workflow (Δ9): el nodo es un cambio PENDIENTE del editor.
   */
  const dropLibraryOperation = useCallback(async (libraryNodeId, position, { sourceProcessCode = '', sourceProcessVersionNumber = null, takenCodes = [] } = {}) => {
    let detail
    try {
      detail = await getOperationVersionDetail(libraryNodeId)
    } catch (thrown) {
      showAlert({ title: t_(readOperationLibraryError(thrown).error || 'Operation version not found'), severity: 'error' })
      return NOT_CREATED
    }
    const version = detail?.version || {}
    // El job del nodo es una COPIA del de la versión, sin la identidad de las filas de biblioteca:
    // el vínculo es lógico y la copia es física (D-01 b). Mandar los ids de biblioteca hacía que el
    // guardado no pudiera distinguir el job del nodo del de la operación.
    const jobReference = asDroppedOperationJob(detail?.jobReference || {})
    // Ajuste del PO (2026-09-15): el nodo se identifica por la OPERACIÓN que se arrastró, no por su
    // tecnología. El code sigue siendo propio del nodo —`library_code` es nulo en un nodo de
    // workflow (S43)— pero es el de la operación TAL CUAL, y solo gana sufijo si ese code ya está en
    // el workflow: "Prueba", "Prueba-1", "Prueba-2"… El nombre es el de la operación.
    const codeBase = version.code || jobReference?.technology?.name || ''
    const code = uniqueOperationNodeCode(codeBase, [
      ...(workflowData.workflow?.nodes || []).map((node) => node.code),
      // S24: en un multi-drop los nodos anteriores aún no están en el modelo (el dispatch no ha
      // cerrado el ciclo), así que sus codes viajan aparte para no repetirse entre ellos.
      ...takenCodes
    ])
    const id = getRandomUUID()
    const node = createNode(id, code, jobReference, null, position, 'JOB', position)
    node.data.nodeName = version.name || ''
    const operationLink = {
      intent: 'BIND',
      libraryNodeId,
      linked: true,
      code: version.code || '',
      name: version.name || '',
      versionNumber: version.versionNumber ?? null,
      latestVersionNumber: version.versionNumber ?? null,
      updateAvailable: false
    }
    dispatchWorkflowData({
      type: 'ADD_NODE',
      node: node.data,
      nodeId: id,
      jobReference,
      subWorkflow: null,
      position: node.position,
      operationLink,
      // Historia 152 (S12/S13/S94/S95 · `RN-01.10`, `RN-01.10b`, D10 + D12): el proceso de origen
      // solo lo tiene lo que se arrastra DEL ÁRBOL de una plantilla, y es el PAR entero. Desde la
      // sección Operations los DOS campos quedan vacíos —nunca uno solo (`IN16`, S94)—, y esa es la
      // única diferencia entre los dos caminos (S12).
      ...toSourceProcessPair(sourceProcessCode, sourceProcessVersionNumber)
    })
    setNodes((nds) => nds.concat(node))
    return { id, code }
  }, [createNode, dispatchWorkflowData, setNodes, showAlert, t_, workflowData.workflow])

  /**
   * Historia 152 (S12 · `RN-01.5`, D8): la versión a la que se vincula un JOB arrastrado DEL ÁRBOL
   * es la misma que se arrastraría desde la sección Operations —la `APPROVED` de número más alto—,
   * y quien la resuelve es N6, igual que para la paleta. El árbol solo dice de qué OPERACIÓN es el
   * nodo (`operationCode`), nunca a qué versión vincularse: la regla es una sola (`RN-01.5`).
   */
  const offeredVersionNodeId = useCallback(async (operationCode) => {
    try {
      const palette = await getOperationPalette(operationCode) || []
      return palette.find((item) => item?.code === operationCode)?.nodeId || ''
    } catch (thrown) {
      return ''
    }
  }, [])

  /**
   * Historia 152 (S15 · `RN-01.8`, `RN-01.8b`, intención `COPY` de §8.3): un JOB LOCAL del árbol
   * nace como copia EMBEBIDA. La copia la hace **Sun** dentro del guardado —los adjuntos no viajan
   * en el `JobReference` (S16)—, así que el cliente no compone nada: manda el nodo con el job que
   * previsualiza y, en `operationLink`, qué job hay que copiar.
   *
   * **Enmienda 2** (PO, 22/09/2026): el nodo ya no nace hueco. `sourceJobReference` es el job del
   * nodo de origen, leído del workflow que lo contiene por quien orquesta el drop (ahí vive la
   * caché del multi-drop, S24); vacío ⇒ el placeholder de siempre (degradación, punto 3).
   *
   * ⚠️ `sourceNodeId`/`sourceWorkflowId` NO son datos de origen: son parámetros TRANSITORIOS de la
   * copia (D11-bis, R15). El único dato de origen que se persiste es `sourceProcessCode`.
   */
  const dropProcessLocalJob = useCallback((payload, position, takenCodes = [], sourceJobReference = {}) => {
    const jobReference = asCopiedJobPreview(sourceJobReference, { indexValues, progressCalculationTypes })
    const code = uniqueOperationNodeCode(payload.code || '', [
      ...(workflowData.workflow?.nodes || []).map((node) => node.code),
      ...takenCodes
    ])
    const id = getRandomUUID()
    const node = createNode(id, code, jobReference, null, position, 'JOB')
    node.data.nodeName = payload.name || ''
    dispatchWorkflowData({
      type: 'ADD_NODE',
      node: node.data,
      nodeId: id,
      jobReference,
      subWorkflow: null,
      position: node.position,
      operationLink: {
        intent: 'COPY',
        sourceNodeId: payload.nodeId,
        sourceWorkflowId: payload.workflowId,
        // PO, 29/09/2026: the copy can be edited before saving; Sun keeps it and brings only the attachments.
        ...copyOfEditableContent(sourceJobReference, jobReference)
      },
      // D12: la copia local sella el MISMO par que un JOB vinculado — el origen es del nodo, no del
      // vínculo, y una copia no tiene vínculo ninguno (`RN-G5`, S15).
      ...toSourceProcessPair(payload.sourceProcessCode, payload.sourceProcessVersionNumber)
    })
    setNodes((nds) => nds.concat(node))
    return { id, code }
  }, [createNode, dispatchWorkflowData, indexValues, progressCalculationTypes, setNodes, workflowData.workflow])

  /**
   * Historia 152 (S20 · `RN-01.9`, `RN-01.10`, `RN-01.10b`): soltar un proceso COMPLETO —la raíz
   * del árbol o un nodo WORKFLOW intermedio— como nodo subproceso. Es el camino de un arrastre
   * suelto y el de cada rama marcada en una selección múltiple del árbol.
   *
   * `takenCodes` son los códigos que ya ha repartido el mismo drop múltiple y que el estado de los
   * nodos aún no refleja. Devuelve el código del nodo creado, o `''` si no se ha podido soltar.
   *
   * `workflows` es solo la PÁGINA de plantillas que la paleta tiene cargada: un subproceso interno
   * del árbol casi nunca está en ella, así que si no aparece se lee por su id.
   */
  const dropSubWorkflow = useCallback(async (subWorkflowId, position, { sourceProcessCode = '', sourceProcessVersionNumber = null, takenCodes = [] } = {}) => {
    let droppedWorkflow = (workflows || []).find(wf => wf.id.toString() === String(subWorkflowId)) || {}
    if (!droppedWorkflow.id && subWorkflowId) {
      try {
        droppedWorkflow = (await fetchWorkflowForProcessTree(subWorkflowId)) || {}
      } catch (thrown) {
        droppedWorkflow = {}
      }
    }
    if (!droppedWorkflow.id) {
      showAlert({
        title: t_('Error: selected workflow not found'),
        severity: 'error',
        message: ''
      })
      return NOT_CREATED
    }

    const currentWorkflowId = workflowData.workflow?.id
    if (String(droppedWorkflow.id) === String(currentWorkflowId)) {
      showAlert({
        title: t_('You cannot insert a workflow into itself'),
        severity: 'error',
        message: ''
      })
      return NOT_CREATED
    }
    const candidateSubWorkflowIds = droppedWorkflow?.subWorkflowIds || []
    if (!canAddWorkflow(currentWorkflowId, droppedWorkflow.id, candidateSubWorkflowIds)) {
      showAlert({
        title: t_('This subworkflow is already present in a parent workflow and cannot be inserted recursively'),
        severity: 'error'
      })
      return NOT_CREATED
    }
    const workflow = {
      id: droppedWorkflow.id,
      // RN01.6 (HU-01): code is the template's identifier; name is the new descriptive field.
      code: droppedWorkflow.code,
      name: droppedWorkflow.name,
      version: droppedWorkflow.version,
      edited: false,
      subWorkflowIds: droppedWorkflow.subWorkflowIds,
      indexValue: droppedWorkflow.indexValue
    }
    // RN01.6 (HU-01): the auto-generated node code derives from the template's code (its "name"
    // is now the new, usually empty descriptive field — deriving from it produced blank codes).
    const counters = countNodeNames(workflow.code)
    let name = generateDefaultName(workflow.code, counters)
    while (takenCodes.includes(name)) {
      name = generateDefaultName(workflow.code, counters)
    }
    const id = getRandomUUID()
    const node = createNode(id, name, null, workflow, position, 'WORKFLOW')
    dispatchWorkflowData({
      type: 'ADD_NODE',
      node: node.data,
      nodeId: id,
      jobReference: null,
      subWorkflow: workflow,
      position: node.position,
      ...toSourceProcessPair(sourceProcessCode, sourceProcessVersionNumber)
    })
    setNodes((nds) => nds.concat(node))
    return { id, code: name }
  }, [workflows, workflowData.workflow, showAlert, t_, canAddWorkflow, countNodeNames, createNode, dispatchWorkflowData, setNodes])

  /**
   * Historia 152 (S12/S15/S24): las filas JOB que se sueltan del árbol de un proceso, una por una y cada una
   * por su camino —vinculada (`BIND`) o local (`COPY`)—, en el orden del árbol. Historia 167: el sitio de
   * cada una lo decide quien llama (`spotOf`: en cascada en el lienzo, encadenadas a la derecha de X en el
   * mini árbol) y devuelve los ids de las que SÍ se crearon, en orden, para enlazarlas (RN-08.7, RN-08.11).
   *
   * **Enmienda 2** (PO, 22/09/2026): aquí vive la lectura del job de origen de las copias locales y su caché,
   * una petición por workflow CONTENEDOR distinto y no una por fila (S24).
   */
  const dropProcessJobs = useCallback(async (payloads, spotOf) => {
    const takenCodes = []
    const createdIds = []
    const sourceWorkflowsById = new Map()
    const sourceJobReferenceOf = async (payload) => {
      const sourceWorkflowId = payload?.workflowId
      if (!sourceWorkflowId) {
        return {}
      }
      if (!sourceWorkflowsById.has(sourceWorkflowId)) {
        try {
          // §8.4: el `workflowId` de la fila del árbol es el workflow que CONTIENE el nodo.
          sourceWorkflowsById.set(sourceWorkflowId, await fetchWorkflowForProcessTree(sourceWorkflowId))
        } catch (thrown) {
          sourceWorkflowsById.set(sourceWorkflowId, {})
        }
      }
      const sourceWorkflow = sourceWorkflowsById.get(sourceWorkflowId) || {}
      const sourceNode = (sourceWorkflow.nodes || []).find((node) => node.id === payload?.nodeId)
      return sourceNode?.jobReference || {}
    }
    for (let index = 0; index < payloads.length; index++) {
      const payload = payloads[index]
      const spot = spotOf(index)
      // #2990: una rama WORKFLOW marcada viaja entera, el subproceso con todo su contenido.
      if (payload?.kind === PROCESS_ROW_DROP_KIND.PROCESS) {
        const created = await dropSubWorkflow(payload.subWorkflowId, spot, {
          sourceProcessCode: payload.sourceProcessCode || '',
          sourceProcessVersionNumber: payload.sourceProcessVersionNumber ?? null,
          takenCodes
        })
        if (created.id) {
          takenCodes.push(created.code)
          createdIds.push(created.id)
        }
        continue
      }
      if (payload?.kind === PROCESS_ROW_DROP_KIND.LOCAL_JOB) {
        const created = dropProcessLocalJob(payload, spot, takenCodes, await sourceJobReferenceOf(payload))
        takenCodes.push(created.code)
        createdIds.push(created.id)
        continue
      }
      const libraryNodeId = await offeredVersionNodeId(payload?.operationCode || '')
      if (!libraryNodeId) {
        showAlert({ title: t_('Operation version not found'), severity: 'error' })
        continue
      }
      const created = await dropLibraryOperation(libraryNodeId, spot, {
        sourceProcessCode: payload?.sourceProcessCode || '',
        sourceProcessVersionNumber: payload?.sourceProcessVersionNumber ?? null,
        takenCodes
      })
      if (created.id) {
        takenCodes.push(created.code)
        createdIds.push(created.id)
      }
    }
    return createdIds
  }, [dropLibraryOperation, dropProcessLocalJob, dropSubWorkflow, offeredVersionNodeId, showAlert, t_])

  /**
   * Soltar una tecnología: la rama de siempre del `onDrop` (#130), con el sitio decidido por quien llama.
   * Devuelve el id del nodo creado. Una plantilla va por `dropSubWorkflow` (#2990), que además la busca por
   * id si no está en la página cargada.
   */
  const dropTechnology = useCallback((payload, position) => {
    const jobReference = createNewJobReference({ technologyName: payload.subType, technologies, indexValues, progressCalculationTypes })
    // P12 (PO): the generated job code reads the technology in the language of whoever creates it (`MONTAJE-1`); the
    // counter still goes by the raw name, so the numbering is one series whatever the language of each creator.
    const name = generateDefaultName(payload.subType, countNodeNames(payload.subType), systemValueLabel(t_, 'technology', jobReference.technology) || payload.subType)
    const id = getRandomUUID()
    const node = createNode(id, name, jobReference, null, position, 'JOB')
    dispatchWorkflowData({
      type: 'ADD_NODE',
      node: node.data,
      nodeId: id,
      jobReference,
      subWorkflow: null,
      position: node.position,
      sourceProcessCode: payload.sourceProcessCode,
      sourceProcessVersionNumber: payload.sourceProcessVersionNumber
    })
    setNodes((nds) => nds.concat(node))
    return id
  }, [countNodeNames, technologies, indexValues, progressCalculationTypes, createNode, dispatchWorkflowData, setNodes, t_])

  /**
   * Lo que crea cada familia no depende de dónde se suelte (D7, CA6.7): esta es la única puerta, para el lienzo
   * y para la fila. Devuelve los ids creados, en orden; vacío si no se creó nada.
   */
  const createDroppedNodes = useCallback(async (payload, spotOf) => {
    if (!payload.subType) {
      return []
    }
    // Historia 130 (S42/S43): una operación de la biblioteca; su contenido se lee aquí (N3).
    if (payload.type === 'operation') {
      const created = await dropLibraryOperation(payload.subType, spotOf(0))
      return created.id ? [created.id] : []
    }
    // Historia 152 (S12/S15/S24): una o varias filas JOB del árbol de un proceso, en el orden del árbol.
    if (payload.type === 'processJobs') {
      let payloads = []
      try {
        payloads = JSON.parse(payload.subType)
      } catch (thrown) {
        return []
      }
      return Array.isArray(payloads) && payloads.length > 0 ? dropProcessJobs(payloads, spotOf) : []
    }
    // Historia 152 (S20) / #2990: una plantilla entera entra como subproceso; se busca por id si no está en la página.
    if (payload.type === 'workflow') {
      const created = await dropSubWorkflow(payload.subType, spotOf(0), {
        sourceProcessCode: payload.sourceProcessCode,
        sourceProcessVersionNumber: payload.sourceProcessVersionNumber
      })
      return created.id ? [created.id] : []
    }
    const id = dropTechnology(payload, spotOf(0))
    return id ? [id] : []
  }, [dropLibraryOperation, dropProcessJobs, dropSubWorkflow, dropTechnology])

  // --- el arrastre de la biblioteca (historia 167, HU-05 · RN-09) ----------------------------------
  const startLibraryDrag = useCallback((choice) => {
    setLibraryDrag({ active: true, label: choice.label })
  }, [])

  const endLibraryDrag = useCallback(() => {
    setLibraryDrag(NO_LIBRARY_DRAG)
    setDropTarget(NO_DROP_TARGET)
  }, [])

  /**
   * Over the canvas the library only says where the piece will go ("Dropped loose on the canvas"). There is no ghost
   * any more (PO, 29/09/2026: the canvas behaves as before this story). Same target again changes no state (R1).
   */
  const onCanvasDragOver = useCallback(() => {
    if (!libraryDragRef.current.active) {
      return
    }
    setDropTarget((current) => (current.kind === DROP_TARGET_KIND.CANVAS ? current : CANVAS_DROP_TARGET))
  }, [])

  const onCanvasDragLeave = useCallback(() => {
    setDropTarget((current) => (current.kind === DROP_TARGET_KIND.CANVAS ? NO_DROP_TARGET : current))
  }, [])

  /** RN-08.3: en el lienzo el nodo aparece donde se suelta, sin enlazar, exactamente como hoy. */
  const onCanvasDrop = useCallback((event, position) => {
    const payload = readDropPayload(event.dataTransfer)
    endLibraryDrag()
    return createDroppedNodes(payload, cascadeFrom(position))
  }, [createDroppedNodes, endLibraryDrag])

  /**
   * D5 (RN-08.4-RN-08.7): sobre la fila de X del mini árbol, el nodo (o nodos) nace a la derecha de X (D16.6) y,
   * CUANDO EXISTE, se enlaza detrás de X de una vez. Si no se crea ninguno, X queda como estaba (RN-08.11).
   * El lienzo se PARCHEA — la arista de X cambia de destino y entran las del nuevo —, nunca se reconstruye.
   */
  const dropAfterNode = useCallback(async (event, afterNodeId) => {
    const payload = readDropPayload(event.dataTransfer)
    endLibraryDrag()
    const afterCanvasNode = nodes.find((node) => node.id === afterNodeId)
    const afterNode = (workflowData.workflow?.nodes || []).find((node) => node.id === afterNodeId)
    if (!afterCanvasNode || !afterNode) {
      return []
    }
    const insertedIds = await createDroppedNodes(payload, (index) => positionRightOf(afterCanvasNode, index))
    if (insertedIds.length === 0) {
      return []
    }
    const plan = planInsertAfter(afterNode, insertedIds, getRandomUUID)
    dispatchWorkflowData({ type: 'INSERT_NODES_AFTER', plan })
    const toEdge = (sourceNodeId, result) => edgeOfNodeResult(sourceNodeId, result, {
      onDeleteEdge: deleteEdge,
      onUpdateEdge: updateEdge,
      workflowInUse: workflowData.workflow?.workflowIsInUse === true
    })
    setEdges((current) => applyInsertionToEdges(current, plan, toEdge))
    return insertedIds
  }, [endLibraryDrag, nodes, workflowData.workflow, createDroppedNodes, dispatchWorkflowData, deleteEdge, updateEdge, setEdges])

  const dropDestination = useMemo(() => destinationOf(dropTarget), [dropTarget])

  const changeLibraryFamily = useCallback((family) => updatePanelPreferences({ family }), [updatePanelPreferences])
  return (
    <div ref={canvasContainerRef} style={{ display: 'grid', height: '100%', position: 'relative' }}>
      <SmartReactflow
        nodes={nodes}
        edges={edges}
        setNodes={setNodes}
        setEdges={setEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        editMode={workflowData.editing}
        onNodeSelectionChange={onNodeSelectionChange}
        fitView={fitView}
        disableFitOnResize
        onDrop={onCanvasDrop}
        onEdgeDeleteEvent={deleteEdge}
        onAddEdgeEvent={addEdge}
        onUpdateEdge={updateEdge}
        onNodeDoubleClick={onSubWorkflowDoubleClick}
        onCanvasDragOver={onCanvasDragOver}
        onCanvasDragLeave={onCanvasDragLeave}
      />
      {/* Historia 152 (S86/S87 · CA3.6): el interruptor y la leyenda del resaltado por proceso de origen viven
          SOBRE EL LIENZO, que es lo que colorean. Solo exigen `WORKFLOWS` (S89). */}
      <div style={{ position: 'absolute', right: '0.5rem', bottom: '0.5rem', zIndex: 5 }}>
        <ProcessOriginLegend
          enabled={processOriginHighlightOn}
          onEnabledChange={setProcessOriginHighlightOn}
          legend={processOriginLegend}
        />
      </div>
      {/* Historia 167 (D0): el panel de proceso y biblioteca, en el sitio de la paleta. */}
      <ProcessLibraryPanel
        preferences={panelPreferences}
        onPreferencesChange={updatePanelPreferences}
        editing={workflowData.editing}
        canvasHeight={canvasHeight}
        processTree={
          <ProcessMiniTree
            workflow={workflowData.workflow}
            effectiveWorkflow={censusWorkflow}
            failedSetNodeIds={censusHydration.failedSetNodeIds}
            failedSetMessages={censusHydration.failedMessageByNodeId}
            currentNodeId={workflowData.currentNode?.id || ''}
            dispatchWorkflowData={dispatchWorkflowData}
            nodes={nodes}
            setNodes={setNodes}
            onSubWorkflowDoubleClick={onSubWorkflowDoubleClick}
            showAlert={showAlert}
            dragActive={libraryDrag.active}
            dropTargetRowKey={dropTarget.rowKey}
            dropTargetKind={dropTarget.kind}
            onDropTargetChange={setDropTarget}
            onDropAfter={dropAfterNode}
            onOpenJobProperties={onOpenJobProperties}
            onOpenJobResources={onOpenJobResources}
          />
        }
        library={
          <LibraryFamilies
            visible={panelOpen && panelPreferences.tab !== PANEL_TAB.PROCESS}
            family={panelPreferences.family}
            onFamilyChange={changeLibraryFamily}
            census={compositionCensus}
            fetchTemplatePage={fetchTemplatePage}
            fetchTechnologyPage={fetchTechnologyPage}
            dropDestination={dropDestination}
            dragActive={libraryDrag.active}
            onItemDragStart={startLibraryDrag}
            onItemDragEnd={endLibraryDrag}
          />
        }
      />
    </div>
  )
}

export default WorkflowFlow
